import {
  hardcodedPronunciationReadingAtSlot,
  type Level,
} from "@-/dev.improve.haniverse.lib.level";
import type { MorphemeGlyphTileGraphemeMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { MorphemeTileCard } from "@-/dev.improve.haniverse.lib.morpheme.stage";
import type {
  BatchedGraphemeTileData,
  GetGraphemeCardStage,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyphs";
import { useMemo } from "react";

/** Which of a level's two card lists a surface shows: its characters, or its `N-Pk` readings. */
export type LevelTilePart = "character" | "pronunciation";

/**
 * The study card a level's tile stands for. A pronunciation level's tile is the pronunciation card
 * its lesson enrolls: the slot's curated reading (干 gan4, then 干 gan1), else the weakest of the
 * character's readings in study. A meaning-only level's tile is the meaning card. A level that
 * teaches meaning and pronunciation together has no single card, so its tile is the grapheme as a
 * whole (`undefined`).
 */
export function getLevelTileCard({
  grapheme,
  level,
  part,
  slotIndex,
}: {
  grapheme: string;
  level: Level;
  part: LevelTilePart;
  /** The tile's position in the level's list for `part`. */
  slotIndex: number | undefined;
}): MorphemeTileCard | undefined {
  if (part === "pronunciation") {
    const reading =
      slotIndex === undefined
        ? undefined
        : hardcodedPronunciationReadingAtSlot({ afterLevel: level.index + 1, grapheme, slotIndex });
    return { kind: "pronunciation", pinyin: reading?.pinyin };
  }
  return level.meaningOnly === true ? { kind: "meaning" } : undefined;
}

/**
 * A pronunciation card's slice: only the taught reading tints the glyph. Alternate-pronunciation
 * dots belong to tiles that represent the whole grapheme, not a specific reading.
 */
export function graphemeSliceForReading(
  slice: MorphemeGlyphTileGraphemeMorphemeSlice,
  pinyin: string,
): MorphemeGlyphTileGraphemeMorphemeSlice {
  if (slice.primaryPinyin === pinyin && slice.additionalPinyin.length === 0) {
    return slice;
  }
  return {
    ...slice,
    additionalPinyin: [],
    primaryPinyin: pinyin,
  };
}

/**
 * `tileData` for a grid of one level's `part`, whose tiles follow `getLevelTileCard`: a learned
 * meaning never shades the tile of a reading that is still to come, and a pronunciation tile's
 * glyph takes its own reading's tone colour (干 gan4 purple, then 干 gan1 red), not the
 * character's primary reading. `getSlice` and `getStage` take the tile's slot in the level.
 */
export function useLevelTileData({
  getCardStage,
  level,
  part,
  tileData,
}: {
  getCardStage: GetGraphemeCardStage;
  level: Level;
  part: LevelTilePart;
  tileData: BatchedGraphemeTileData;
}): BatchedGraphemeTileData {
  // A slot's slice keeps its identity while the grapheme's own slice does, so tiles stay memoized.
  const sliceByReading = useMemo(
    () =>
      new WeakMap<
        MorphemeGlyphTileGraphemeMorphemeSlice,
        Map<string, MorphemeGlyphTileGraphemeMorphemeSlice>
      >(),
    [],
  );
  return useMemo(
    () => ({
      getSlice: (grapheme, slotIndex) => {
        const slice = tileData.getSlice(grapheme, slotIndex);
        const card = getLevelTileCard({ grapheme, level, part, slotIndex });
        const pinyin = card?.kind === "pronunciation" ? card.pinyin : undefined;
        if (pinyin === undefined) {
          return slice;
        }
        let byReading = sliceByReading.get(slice);
        if (byReading === undefined) {
          byReading = new Map();
          sliceByReading.set(slice, byReading);
        }
        let readingSlice = byReading.get(pinyin);
        if (readingSlice === undefined) {
          readingSlice = graphemeSliceForReading(slice, pinyin);
          byReading.set(pinyin, readingSlice);
        }
        return readingSlice;
      },
      getStage: (grapheme, slotIndex) => {
        const card = getLevelTileCard({ grapheme, level, part, slotIndex });
        return card === undefined
          ? tileData.getStage(grapheme, slotIndex)
          : getCardStage(grapheme, card);
      },
    }),
    [getCardStage, level, part, sliceByReading, tileData],
  );
}
